import { Places } from "./types";

const scriptParams = {
  key: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
  libraries: "places",
  loading: "async",
  callback: "googlePlacesLoaded"
};

const scriptHost = "https://maps.googleapis.com/maps/api/js";

let placesPromise: Promise<Places> | null = null;

async function init(div: HTMLDivElement) {
  await new Promise<void>((resolve) => {
    // @ts-ignore
    window["googlePlacesLoaded"] = () => {
      resolve();
    };
  });
  const places = (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;

  const details = new places.PlacesService(div);
  const autocomplete = new places.AutocompleteService();

  return {
    getPlacePredictions(...args: Parameters<Places["getPlacePredictions"]>) {
      return autocomplete.getPlacePredictions(...args);
    },

    getDetails(...args: Parameters<Places["getDetails"]>) {
      return details.getDetails(...args);
    }
  };
}

/**
 * Inject a script tag to load the google maps library.
 * then initialize the services we need and return proxy functions to call them.
 * That's mostly to simplify mocking.
 * requires a `div` for "attribution".
 */

export default function getPlaces(div: HTMLDivElement): Promise<Places> {
  if (import.meta.env.NODE_ENV === "test" || import.meta.env.VITEST) {
    return Promise.resolve({
      getPlacePredictions: () => {
        return Promise.resolve({ predictions: [] });
      },
      getDetails: (
        _: unknown,
        callback: (
          result: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus
        ) => void
      ) => {
        callback(null, "OK" as google.maps.places.PlacesServiceStatus);
      }
    });
  }

  if (!placesPromise) {
    placesPromise = new Promise((resolve) => {
      const scriptTag = document.createElement("script");
      scriptTag.src = [scriptHost, new URLSearchParams(scriptParams)].join("?");
      scriptTag.async = true;
      scriptTag.addEventListener("load", () => {
        init(div).then(resolve);
      });
      document.head.appendChild(scriptTag);
    });
  }
  return placesPromise;
}
